import React from "react";
import { useNavigate } from "react-router";
import { selectors } from "@ReduxStore/reducers";
import { useSelector, useDispatch } from "react-redux";
import { Creators } from "@ReduxStore/actionTypes";
import { ONLINE_OPTIONS, ROUTES, categoryKeys } from "@Src/constants";
import { useSyncDealsCtx } from "./useSyncDealsCtx";

export function useCategoriesAndPromotions() {
  const headerCats = useSelector(selectors.getHeaderCategories);
  const dropdownCategories = useSelector(selectors.getDropdownCategories);
  const playSubCategories = useSelector(selectors.getPlayCategories);
  const shopSubCategories = useSelector(selectors.getShopCategories);
  const eatSubCategories = useSelector(selectors.getEatCategories);
  const travelCategories = useSelector(selectors.getTravelCategories);
  const rootCategoryIds = useSelector(selectors.getRootCategoryFilterIds);
  const hotDeals = useSelector(selectors.getHotDeals);
  const ourPicks = useSelector(selectors.getOurPicks);
  const hotDealsApiStatus = useSelector(selectors.getHotDealsApiStatus);
  const ourPicksApiStatus = useSelector(selectors.getOurPicksApiStatus);

  const ormCatRoot = useSelector(selectors.rootCategories);
  const ormCatSub = useSelector(selectors.subCategories);
  const ormCatMap = [...ormCatRoot, ...ormCatSub];
  const navigate = useNavigate();

  const dispatch = useDispatch();

  const {
    handleClickItem: handleDealOrPromotion,
    context: dealsModalContext,
    setContextCallback: setDealsModalContextCallback,
    storeKey,
    dealModalOpen,
    handleCloseModal,
    mobileMenuOpen,
    closeMobileMenu,
    toggleMobileMenu,
  } = useSyncDealsCtx();

  // Get Our Picks and Hot Deals
  React.useEffect(() => {
    const hasHotDeals = dropdownCategories?.find(
      (category) => category.category_key === categoryKeys.HOT_DEALS,
    );

    if (hasHotDeals && !hotDealsApiStatus) {
      dispatch(Creators.fetchHotDeals());
    }
    if (!ourPicksApiStatus) {
      dispatch(Creators.fetchOurPicks());
    }
  }, [dispatch, hotDealsApiStatus, ourPicksApiStatus, dropdownCategories]);

  const handleSetFilters = (clickedCat) => {
    dispatch(Creators.selectCategory(clickedCat.rootCategory));
    dispatch(Creators.filterCategoryChange(clickedCat.rootCategory?.id));

    if (clickedCat.subCategory) {
      // If root category is already set, just set the sub category
      if (rootCategoryIds.includes(clickedCat.rootCategory?.id)) {
        dispatch(Creators.selectSubCategory(clickedCat.subCategory));
        // Otherwise, set a future sub category so we will fetch the root category first
      } else {
        dispatch(Creators.selectFutureSubCategory(clickedCat.subCategory));
      }
    }

    if (clickedCat.sortBy) {
      dispatch(Creators.filterChangingHowToSort(clickedCat.sortBy));
    }

    if (clickedCat.onlineOnly) {
      dispatch(Creators.filterChangingOnline(ONLINE_OPTIONS.ONLY));
    }
  };

  const itemClickHandler = (clickedItem, callback) => {
    const initiateSearch = () => {
      dispatch(Creators.selectPhrase(clickedItem.name));
      navigate(ROUTES.SEARCH_PATH);
    };
    const handleRedirect = (item) => {
      navigate(item.route, {
        state: item.state || {},
      });
      if (item.start_tab) {
        dispatch(
          Creators.addTravelStartTab(
            Array.isArray(item.start_tab) ? item.start_tab[0] : item.start_tab,
          ),
        );
      }
    };

    const handleCategory = () => {
      const clickedCat = (() => {
        // Reducer needs to read the ORM category, so map through to get the right one/s
        // Subcategory is clicked
        if (clickedItem.category_parent_key) {
          return {
            ...clickedItem,
            rootCategory: ormCatMap.filter((cat) => cat.id === clickedItem.category_parent_key)[0],
            subCategory: ormCatMap.filter((cat) => cat.id === clickedItem.category_key)[0],
          };
        }
        return {
          ...clickedItem,
          rootCategory: ormCatMap.filter((cat) => cat.id === clickedItem.category_key)[0],
          subCategory: null,
        };
      })();

      handleSetFilters(clickedCat);
      navigate(ROUTES.SEARCH_PATH);
    };

    if (callback) callback();
    if (clickedItem.store_key) {
      // Item clicked needs to open the deals modal
      handleDealOrPromotion(clickedItem);
    } else {
      // Item clicked needs to initiate a search with the name
      if (clickedItem.initiateSearch) {
        initiateSearch();
      } else {
        if (clickedItem.attraction_id) {
          // Item clicked is an attraction
          const route = clickedItem.retailer_id ? ROUTES.RETAILER_PATH : ROUTES.PARKS_PATH;
          navigate(route, {
            state: clickedItem,
          });
        } else if (clickedItem.route) {
          // Item clicked needs an internal redirect other than search, ex: deal or travel route
          handleRedirect(clickedItem);
        } else {
          // Item clicked is a search category
          handleCategory();
        }
      }
    }
  };

  return {
    itemClickHandler,
    headerCats,
    playSubCategories,
    eatSubCategories,
    shopSubCategories,
    travelCategories,
    dropdownCategories,
    hotDeals,
    rootCategoryIds,
    ormCatRoot,
    ormCatSub,
    dealsModalContext,
    setDealsModalContextCallback,
    storeKey,
    dealModalOpen,
    handleCloseModal,
    ourPicks,
    mobileMenuOpen,
    closeMobileMenu,
    toggleMobileMenu,
  };
}
